import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { throttle, debounce } from "throttle-debounce";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Table from "../../_common/Table";
import contractorActions from "../../../_actions/contractorActions";
import "../styles/adminStyles.css";
import "./adminContractors.css";
import AdminContractorDetails from "./AdminContractorDetails";
import { CONTRACTOR } from "../../../_constants/actionConstants";
import AdminContractorsDetailsModal from "./AdminContractorsDetailsModal";
import EditContractorModalV2 from "./EditContractorModalV2";

export function AdminContractors({ tabId, permissions }) {
  const dispatch = useDispatch();
  const fetching = useSelector((state) => state.contractors.fetching);
  const contractorList = useSelector(
    (state) => state.contractors.contractorList[tabId] || null
  );

  const [nameFilter, setNameFilter] = useState("");
  const [sortColumn, setSortColumn] = useState({ path: "name", order: "asc" });
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showEditContractorModal, setShowEditContractorModal] = useState(false);
  const [showContractorDetailsModal, setShowContractorDetailsModal] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [currPage, setCurrPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  // Kept as a ref so the throttled/debounced handleSearch always reads the latest value
  // without needing to be recreated on every nameFilter change.
  const nameFilterRef = useRef(nameFilter);

  useEffect(() => {
    const init = async () => {
      dispatch({ type: CONTRACTOR.SET_SEARCH_WAITINGFOR, tabId, waitingFor: "" });
      const count = await dispatch(contractorActions.getPaged(tabId, 1, 50, "", true));
      setPageCount(count);
    };
    init();
    return () => {
      dispatch({ type: CONTRACTOR.CLEAR_MISC_SESSION_DATA, tabId });
      dispatch({ type: CONTRACTOR.CLEAR_LIST, tabId });
      dispatch({ type: CONTRACTOR.CLEAR_ALL_DETAILS });
    };
  }, []);

  // To be called only by the throttler/debouncer. SET_WAITINGFOR has already been
  // dispatched prior to this function. Reads nameFilterRef to avoid stale closure.
  const handleSearch = async () => {
    const count = await dispatch(
      contractorActions.getPaged(tabId, 1, 50, nameFilterRef.current, true)
    );
    setPageCount(count);
    setCurrPage(1);
  };

  const searchThrottled = useRef(throttle(250, handleSearch));
  const searchDebounced = useRef(debounce(250, handleSearch));

  const refreshList = async (skipDetails = false) => {
    const count = await dispatch(
      contractorActions.getPaged(tabId, 1, 50, nameFilter, true)
    );
    if (!skipDetails && selectedContractor)
      dispatch(contractorActions.getDetails(selectedContractor.contractorId));
    setCurrPage(1);
    setPageCount(count);
  };

  const clearDetails = (contractor) => {
    if (contractor)
      dispatch({ type: CONTRACTOR.CLEAR_DETAILS, contractorId: contractor.contractorId });
    setSelectedContractor(null);
  };

  const getNextPage = async () => {
    const count = await dispatch(
      contractorActions.getPaged(tabId, currPage + 1, 50, nameFilter, false)
    );
    setCurrPage(currPage + 1);
    setPageCount(count);
  };

  const handleClick_editFromRow = (contractor) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNew(false);
    setSelectedContractor(contractor);
    await dispatch(contractorActions.getDetails(contractor.contractorId));
    setShowEditContractorModal(true);
  };

  const handleClick_detailsFromRow = (contractor) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsNew(false);
    setSelectedContractor(contractor);
    await dispatch(contractorActions.getDetails(contractor.contractorId));
    setShowContractorDetailsModal(true);
  };

  const handleChange_nameFilter = async (e) => {
    const value = e.target.value;
    setNameFilter(value);
    nameFilterRef.current = value;
    dispatch({ type: CONTRACTOR.SET_SEARCH_WAITINGFOR, tabId, waitingFor: value });

    if (!value) {
      const count = await dispatch(contractorActions.getPaged(tabId, 1, 50, "", true));
      setCurrPage(1);
      setPageCount(count);
    } else if (value.length < 3) {
      searchThrottled.current();
    } else {
      searchDebounced.current();
    }
  };

  const handleClick_tr = (contractor) => {
    const mustReloadDetails =
      !selectedContractor ||
      selectedContractor.contractorId !== contractor.contractorId;
    setSelectedContractor(contractor);
    if (mustReloadDetails)
      dispatch(contractorActions.getDetails(contractor.contractorId));
  };

  const handleClick_refresh = (e) => {
    e.preventDefault();
    refreshList();
    clearDetails(selectedContractor);
  };

  const handleClick_add = (e) => {
    e.preventDefault();
    setIsNew(true);
    setShowEditContractorModal(true);
  };

  const deleteContractor = async (contractor) => {
    const deleted = await dispatch(contractorActions.deleteContractor(contractor.contractorId));
    if (deleted) {
      clearDetails(selectedContractor);
      refreshList(true);
    }
  };

  const handleClick_edit = (e) => {
    e.preventDefault();
    setIsNew(false);
    setShowEditContractorModal(true);
  };

  const columns = useMemo(
    () => [
      {
        path: "name",
        label: "Contractor Name",
        thClassName: "admin-contractors-table__th--name",
        tdClassName: "admin-contractors-table__th--name",
      },
      {
        path: "abbreviation",
        label: "Abbrev.",
        thClassName: "admin-contractors-table__th--abbrev",
        tdClassName: "admin-contractors-table__th--abbrev",
      },
      {
        path: "projectCount",
        label: "Projects",
        thClassName: "admin-contractors-table__th--projectcount",
        tdClassName: "admin-contractors-table__th--projectcount",
      },
      {
        path: "edit",
        label: "",
        content: (contractor) => (
          <button
            type="button"
            name="editContractor"
            id={`editContractor_${contractor.contractorId}`}
            className="btn btn-sm btn-secondary"
            onClick={handleClick_editFromRow(contractor)}
            onTouchEnd={handleClick_editFromRow(contractor)}
            disabled={fetching}
          >
            Edit...
            {fetching &&
              selectedContractor &&
              selectedContractor.contractorId === contractor.contractorId && (
                <FontAwesomeIcon icon="spinner" className="fa-fw ml-1" spin={fetching} />
              )}
          </button>
        ),
        thClassName: "admin-contractors-table__th--edit",
        tdClassName: "admin-contractors-table__th--edit",
        disableSort: true,
      },
      {
        path: "details",
        label: "",
        content: (contractor) => (
          <button
            type="button"
            name="contractorDetails"
            id={`contractorDetails_${contractor.contractorId}`}
            className="btn btn-sm btn-info"
            onClick={handleClick_detailsFromRow(contractor)}
            onTouchEnd={handleClick_detailsFromRow(contractor)}
            disabled={fetching}
          >
            Details
            {fetching &&
              selectedContractor &&
              selectedContractor.contractorId === contractor.contractorId && (
                <FontAwesomeIcon icon="spinner" className="fa-fw ml-1" spin={fetching} />
              )}
          </button>
        ),
        thClassName: "admin-contractors-table__th--details",
        tdClassName: "admin-contractors-table__th--details",
        disableSort: true,
      },
    ],
    [fetching, selectedContractor]
  );

  return (
    <React.Fragment>
      <h2 className="mb-1">Contractors</h2>
      <hr className="mt-0 mb-2" />
      <div className="d-flex flex-row w-100">
        <div className="admin-list pr-1">
          <div className="form-inline mb-2">
            <button
              type="button"
              name="refreshContractorData"
              id="refreshContractorData"
              className="btn btn-secondary mr-2"
              onClick={handleClick_refresh}
            >
              <FontAwesomeIcon icon="sync-alt" className="fa-fw mr-1" />
              Refresh
            </button>
            <button
              type="button"
              name="addContractor"
              id="addContractor"
              className="btn btn-info mr-2"
              onClick={handleClick_add}
            >
              <FontAwesomeIcon icon="plus" className="fa-fw mr-1" />
              New...
            </button>
          </div>
          <div className="d-flex flex-row flex-wrap w-100 mb-1 admin-filter-group">
            <div className="admin-filter-item admin-filter-item__input flex-grow-1">
              <div className="input-group">
                <div className="input-group-prepend">
                  <span className="input-group-text bg-lightgray">
                    <FontAwesomeIcon icon="search" className="fa-fw text-muted" />
                  </span>
                </div>
                <input
                  name="nameFilter"
                  id="nameFilter"
                  className="form-control"
                  value={nameFilter}
                  onChange={handleChange_nameFilter}
                  placeholder="Contractor Name"
                />
              </div>
            </div>
          </div>
          <div className="d-flex flex-row w-100 mb-1">
            <div className="alert alert-warning p-2 w-100 mb-0">
              <h6>
                <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mr-2" />
                Updating Contractors
              </h6>
              <ul className="mb-0">
                <li>Below list shows ALL contractors currently added to ProDMS.</li>
                <li>Changes made here may affect existing data, such as daily reports.</li>
                <li>
                  Contractors can be deleted only if it is unassigned from all
                  current/ongoing projects (go to Project Admin {`->`} Contractors).
                  Contractors cannot be deleted if it is assigned to any past/completed
                  project. You can view the past and ongoing projects for this contractor
                  in the details panel.
                </li>
              </ul>
            </div>
          </div>
          <div className="admin-list__table--wrapper border">
            <Table
              className="table table-sm table-hover admin-list__table"
              data={contractorList || []}
              idPropName="contractorId"
              selectedItemId={selectedContractor && selectedContractor.contractorId}
              columns={columns}
              sortColumn={sortColumn}
              onSort={setSortColumn}
              handleClick_tr={handleClick_tr}
              isPaginated={true}
              currPage={currPage}
              pageCount={pageCount}
              getNextPage={getNextPage}
            />
          </div>
        </div>
        <div className="admin-details">
          <div className="card">
            <div className="card-header bg-lightgray">Selected Contractor</div>
            <div className="card-body">
              <AdminContractorDetails
                permissions={permissions}
                contractorId={selectedContractor && selectedContractor.contractorId}
                handleClick_edit={handleClick_edit}
                deleteContractor={deleteContractor}
              />
            </div>
          </div>
        </div>
      </div>
      {showEditContractorModal && (
        <EditContractorModalV2
          showModal={showEditContractorModal}
          toggleModal={() => setShowEditContractorModal(false)}
          data={{ isNew, contractorId: isNew ? null : selectedContractor.contractorId }}
          projectId={null}
          contractorId={isNew ? null : selectedContractor.contractorId}
          listRefreshCallback={refreshList}
        />
      )}
      {showContractorDetailsModal && (
        <AdminContractorsDetailsModal
          permissions={permissions}
          contractorId={selectedContractor && selectedContractor.contractorId}
          handleClick_edit={handleClick_edit}
          deleteContractor={deleteContractor}
          showContractorDetailsModal={showContractorDetailsModal}
          toggleContractorDetailsModal={() => setShowContractorDetailsModal(false)}
        />
      )}
    </React.Fragment>
  );
}

export default AdminContractors;
